import React, { useState, useMemo, forwardRef, useRef } from 'react';
import BaseInput from '../BaseInput/BaseInput';
import BaseInputIcon from '../BaseInput/BaseInputIcon';
import InputControl from '../InputControl/InputControl';
import { BaseTextInputProps } from '../shared-types';
import { noop, useForkRef, useInputComponentIdAndLabelData } from '../utils';

export type Props = BaseTextInputProps & {
  adornmentLeft?: React.ReactNode;
  adornmentRight?: React.ReactNode;
  hideShowPassword?: boolean;
};

const PasswordInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const [isHidden, setIsHidden] = useState(true);

  const {
    label,
    error,
    onChange,
    hint,
    showRequiredAsterisk,
    eventId,
    onHintToggle = noop,
    hideShowPassword,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    adornmentLeft,
    adornmentRight,
    placeholder = '●●●●●●●',
    ...rest
  } = props;
  const inputRef = useRef<HTMLInputElement>();

  const handleRef: React.Ref<HTMLInputElement> = useForkRef(ref, inputRef);

  const hideInputIcon = useMemo(
    () =>
      hideShowPassword ? null : (
        <BaseInputIcon
          aria-label={
            isHidden
              ? `Show ${label || 'Password'}`
              : `Hide ${label || 'Password'}`
          }
          icon={isHidden ? 'eyeOpened' : 'eyeClosed'}
          color={{
            type: 'theme',
            colorLegacy: 'Neutral/90',
            colorNext: 'neutral/default',
          }}
          onClick={() => setIsHidden(!isHidden)}
        />
      ),
    [hideShowPassword, label, isHidden]
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const {
    id,
    containerId,
    labelId,
    'aria-labelledby': ariaLabelledby,
    hintId,
  } = useInputComponentIdAndLabelData('PasswordInput', label, ariaLabel);

  return (
    <InputControl
      id={containerId}
      label={label}
      labelId={labelId}
      errorMessage={error}
      hint={hint}
      hintId={hintId}
      showRequiredAsterisk={showRequiredAsterisk}
      eventId={eventId}
      onHintToggle={onHintToggle}
    >
      <BaseInput
        {...rest}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        data-testid={dataTestId || id}
        aria-describedby={hintId}
        ref={handleRef}
        label={label}
        onChange={handleChange}
        error={!!error}
        componentName="PasswordInput"
        adornmentLeft={adornmentLeft}
        adornmentRight={
          <>
            {hideInputIcon}
            {adornmentRight}
          </>
        }
        type={isHidden ? 'password' : 'text'}
      />
    </InputControl>
  );
});

export default PasswordInput;
